import { useEffect, useMemo, useState } from "react";

import { useTheme } from "@mui/material";

import {
  ModelType,
  StatisticsDataInput,
  TimePeriod,
  eventType,
} from "../../../../../API";
import ApexOptions from "../../../../../common/components/apexchart/ApexChartModels";
import useLocationFromCache from "../../../../../common/hooks/useLocationFromCache";
import { useGetStatisticsData } from "../../system-health/hooks/useGetStatisticsData";
import {
  IChartProps,
  IFalsePositiveEventsResponseModel,
} from "../../system-performance/models/chart";
import { modelToServiceMap } from "../../../../../common/variables/common";
import { ServiceTypeEnum } from "../../../../model-manager/variables/modelManager";

export const useFalsePositiveEventsChart = (
  eventType: eventType,
  models: ModelType[],
  locations: string[] | null,
  timePeriod: TimePeriod
): IChartProps => {
  const theme = useTheme();

  const { getCachedLocationName } = useLocationFromCache();

  const options = useMemo(() => {
    const options: ApexOptions = {
      chart: {
        type: "bar",
        stacked: true,
        toolbar: {
          show: false,
        },
        fontFamily: "Inter",
      },
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: 24,
        },
      },
      theme: {
        mode: theme.palette.mode,
      },
      legend: {
        show: false,
        showForSingleSeries: true,
      },
      colors: ["#F3B344", "#CC7B00"],
      states: {
        hover: {
          filter: {
            type: "darken",
          },
        },
      },
      xaxis: {
        labels: {
          style: {
            colors: theme.palette.text.secondary,
            fontSize: "12px",
            fontWeight: 400,
            fontFamily: "Inter",
          },
        },
      },
    };

    return options;
  }, [theme.palette.mode]);

  const filterParams = useMemo(
    (): Pick<
      StatisticsDataInput,
      "eventType" | "models" | "timePeriod" | "locations"
    > => ({
      eventType,
      models,
      timePeriod,
      locations,
    }),
    [locations, timePeriod]
  );

  const { data, loading, refetching, refetchChartData } =
    useGetStatisticsData<IFalsePositiveEventsResponseModel[]>(filterParams);

  const [chartSeries, setChartProps] = useState<
    ApexAxisChartSeries | ApexNonAxisChartSeries | undefined
  >([]);

  const [chartOptions, setChartOptions] = useState<ApexOptions>(options);

  useEffect(() => {
    const filteredData =
      Array.isArray(locations) && locations.length > 0
        ? data.filter(item => locations.includes(item.location_id))
        : data;

    const parsed = filteredData.map(item => ({
      ...item,
      count: Number(item.count),
      locationName:
        getCachedLocationName(item.location_id) || "Unknown location",
    }));

    const series = generateChartSeries(parsed);

    const extendedOptions = generateChartCategories(parsed, series);

    setChartProps(series);

    setChartOptions(extendedOptions);
  }, [data, theme.palette.mode]);

  const generateChartSeries = (
    response: IFalsePositiveEventsResponseModel[]
  ): ApexAxisChartSeries | ApexNonAxisChartSeries => {
    // Group by model and sum counts
    const modelCounts: Record<string, number> = {};

    response.forEach(item => {
      const modelKey = item.model ?? "Unknown model";

      modelCounts[modelKey] = (modelCounts[modelKey] || 0) + Number(item.count);
    });

    const series = [
      {
        data: Object.values(modelCounts),
      },
    ];

    return series;
  };

  const generateChartCategories = (
    response: IFalsePositiveEventsResponseModel[],
    _series: ApexAxisChartSeries | ApexNonAxisChartSeries
  ): ApexOptions => {
    if (!Array.isArray(response) || response.length === 0) {
      return options;
    }

    const records = response.reduce((acc, curr) => {
      const model = curr.model ?? "Unknown model";
      const locationName = curr.locationName ?? "Unknown location";

      if (!acc[model]) {
        acc[model] = [];
      }

      const existing = acc[model].find(
        item => item.locationName === locationName
      );

      if (existing) {
        existing.count += curr.count;
      } else {
        acc[model].push({
          locationName,
          count: curr.count,
        });
      }

      return acc;
    }, {} as Record<string, { locationName: string; count: number }[]>); // Group by model and location

    const filteredResponse = Object.values(records);

    const availableModels = Array.from(
      new Set(response.map(item => item.model))
    );

    const availableServices: ServiceTypeEnum[] = availableModels.map(
      m => modelToServiceMap[m as ModelType]
    );

    const extendedOptions: ApexOptions = {
      ...options,
      xaxis: {
        ...options.xaxis,
        min: 0,
        labels: {
          formatter: val => (Number.isInteger(val) ? String(val) : ""),
        },
        categories: availableServices.map((svc, idx) => `${idx + 1}. ${svc}`),
      },
      yaxis: {
        labels: {
          offsetX: -22,
          offsetY: availableServices?.length === 1 ? -5 : 0,
          align: "left",
        },
      },
      dataLabels: {
        enabled: true,
        style: {
          colors: ["#fff"],
        },
        offsetX: 0,
      },
      tooltip: {
        enabled: true,
        custom: function (options) {
          const series = options.series as number[][];
          const seriesIndex = options.seriesIndex;
          const dataPointIndex = options.dataPointIndex;

          const totalCount = locations?.length
            ? series[seriesIndex][dataPointIndex]
            : series.reduce((acc, val) => acc + val[dataPointIndex], 0);

          const tooltipData = `${filteredResponse[dataPointIndex]
            .map(
              item =>
                `<div>${item.locationName}: <strong>${item.count}</strong></div>`
            )
            .join("")}`;

          return `
            <div class="apexcharts-tooltip-box">
              <div style="margin-bottom: 8px;">
                <strong>Total: ${totalCount}</strong>
              </div>
              <div style="border-top: 1px solid #ccc; padding-top: 8px;">
                ${tooltipData}
              </div>
            </div>
          `;
        },
      },
    };

    return extendedOptions;
  };

  return {
    series: chartSeries,
    options: chartOptions,
    loading,
    refetching,
    refetchChartData,
    showNoData:
      !loading && !refetching && data?.filter(d => d.count).length === 0,
  };
};
